import { useCallback, useState } from "react";
import { useQueryClient } from "react-query";
import { toast } from "react-toastify";

import { renameFile } from "../api/files.api";
import type {
  FileListItem,
  StorageErrorCode,
} from "../types/project-storage.types";
import { mapStorageError } from "../utils/storageErrors";
import { folderChildrenQueryKey } from "./useFolderChildren";

export type RenameFileResult =
  | { ok: true; file: FileListItem }
  | { ok: false; errorCode?: StorageErrorCode; errorMessage: string };

export function useRenameFile(mmCode: number | null, folderId: string | null) {
  const queryClient = useQueryClient();
  const [isRenaming, setIsRenaming] = useState(false);

  const renameFileItem = useCallback(
    async (fileId: string, displayName: string): Promise<RenameFileResult> => {
      if (mmCode === null) {
        return {
          ok: false,
          errorMessage: "Não foi possível renomear o arquivo.",
        };
      }

      setIsRenaming(true);

      try {
        const file = await renameFile(mmCode, fileId, { displayName });

        if (folderId) {
          await queryClient.invalidateQueries(
            folderChildrenQueryKey(mmCode, folderId)
          );
        }

        toast.success("Arquivo renomeado com sucesso.");
        return { ok: true, file };
      } catch (error) {
        const mapped = mapStorageError(
          error,
          "action",
          "Não foi possível renomear o arquivo."
        );

        return {
          ok: false,
          errorCode: mapped.errorCode,
          errorMessage: mapped.message,
        };
      } finally {
        setIsRenaming(false);
      }
    },
    [folderId, mmCode, queryClient]
  );

  return {
    renameFileItem,
    isRenaming,
  };
}
